import { Button } from '@suscri/ui';
import {
  KeyRound,
  Plus,
  Trash2,
  Webhook,
  Copy,
  Power,
  AlertTriangle,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PageHeader } from '../components/ui/PageHeader';
import { useToast } from '../lib/toast';
import { trpc } from '../lib/trpc';

// Herramientas de desarrollador (M7, plan Business): API keys de la API
// pública REST (/v1) y webhooks salientes firmados.

const API_BASE = 'https://api.suscri.net';

function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  return (
    <button
      onClick={() => {
        navigator.clipboard
          .writeText(value)
          .then(() => toast.success(t('Copiado al portapapeles.')))
          .catch(() => toast.error(t('No se pudo copiar.')));
      }}
      className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
      title={t('Copiar')}
    >
      <Copy className="h-4 w-4" />
    </button>
  );
}

export function DeveloperPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const utils = trpc.useUtils();

  const [keyName, setKeyName] = useState('');
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [newWebhookSecret, setNewWebhookSecret] = useState<string | null>(null);

  const { data: apiKeys } = trpc.developer.listApiKeys.useQuery();
  const { data: webhooks } = trpc.developer.listWebhooks.useQuery();
  const { data: events } = trpc.developer.availableEvents.useQuery();

  const handleError = (error: unknown) => {
    const message = error instanceof Error ? error.message : '';
    if (message.includes('PLAN_REQUIRED')) {
      toast.error(
        t('La API pública y los webhooks son funciones del plan Business.')
      );
    } else {
      toast.error(message || t('No se pudo completar la acción.'));
    }
  };

  const createKey = trpc.developer.createApiKey.useMutation({
    onSuccess: result => {
      setNewSecret(result.secret);
      setKeyName('');
      utils.developer.listApiKeys.invalidate();
    },
    onError: handleError,
  });
  const revokeKey = trpc.developer.revokeApiKey.useMutation({
    onSuccess: () => utils.developer.listApiKeys.invalidate(),
    onError: handleError,
  });
  const createWebhook = trpc.developer.createWebhook.useMutation({
    onSuccess: result => {
      setNewWebhookSecret(result.secret);
      setWebhookUrl('');
      utils.developer.listWebhooks.invalidate();
    },
    onError: handleError,
  });
  const updateWebhook = trpc.developer.updateWebhook.useMutation({
    onSuccess: () => utils.developer.listWebhooks.invalidate(),
    onError: handleError,
  });
  const deleteWebhook = trpc.developer.deleteWebhook.useMutation({
    onSuccess: () => utils.developer.listWebhooks.invalidate(),
    onError: handleError,
  });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('Integraciones')}
        title={t('Desarrollador')}
        description={<>{t('API pública y webhooks salientes — funciones del plan')}{' '}<Link to="/plan" className="font-semibold text-primary hover:underline">Business</Link></>}
      />

      {/* API keys */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md">
            <KeyRound className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">API keys</h2>
            <p className="text-xs text-gray-500">
              {t('Acceso a la API REST:')}{' '}
              <code>{API_BASE}/v1/subscriptions</code> {t('con cabecera')}{' '}
              <code>Authorization: Bearer sk_suscri_…</code>.{' '}
              {t('Guía completa en')} <code>docs/API-PUBLICA.md</code>{' '}
              {t('del repositorio.')}
            </p>
          </div>
        </div>

        {newSecret && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div className="flex-1 text-sm text-amber-900">
              {t('Guarda esta clave ahora:')}{' '}
              <strong>{t('no volverá a mostrarse')}</strong>.
              <div className="mt-1 flex items-center gap-1 font-mono text-xs break-all">
                {newSecret}
                <CopyButton value={newSecret} />
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setNewSecret(null)}
            >
              {t('Entendido')}
            </Button>
          </div>
        )}

        <form
          className="mb-6 flex flex-wrap items-end gap-3"
          onSubmit={e => {
            e.preventDefault();
            if (!keyName) return;
            createKey.mutate({ name: keyName });
          }}
        >
          <div className="flex-1 min-w-[220px]">
            <label className="mb-1 block text-xs font-semibold text-gray-500">
              {t('Nombre de la clave')}
            </label>
            <input
              type="text"
              required
              maxLength={60}
              value={keyName}
              onChange={e => setKeyName(e.target.value)}
              placeholder={t('p. ej. Integración con Zapier')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <Button type="submit" disabled={createKey.isPending}>
            <Plus className="mr-2 h-4 w-4" />
            {t('Crear clave')}
          </Button>
        </form>

        {apiKeys && apiKeys.length > 0 ? (
          <div className="space-y-2">
            {apiKeys.map(key => (
              <div
                key={key.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {key.name}{' '}
                    {key.revokedAt && (
                      <span className="ml-1 rounded bg-red-100 px-2 py-0.5 text-xs text-red-700">
                        {t('revocada')}
                      </span>
                    )}
                  </p>
                  <p className="font-mono text-xs text-gray-500">
                    {key.prefix}…{' '}
                    {key.lastUsedAt
                      ? `· ${t('último uso')} ${new Date(key.lastUsedAt).toLocaleDateString()}`
                      : `· ${t('sin usar')}`}
                  </p>
                </div>
                {!key.revokedAt && (
                  <button
                    onClick={() => revokeKey.mutate({ keyId: key.id })}
                    className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"
                    title={t('Revocar clave')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">{t('Sin claves todavía.')}</p>
        )}
      </div>

      {/* Webhooks salientes */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
            <Webhook className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('Webhooks salientes')}
            </h2>
            <p className="text-xs text-gray-500">
              {t('Recibe un POST firmado (cabecera')}{' '}
              <code>X-Suscri-Signature</code>
              {t(', HMAC-SHA256) cuando ocurren eventos:')}{' '}
              {events?.join(', ') ?? '…'}
            </p>
          </div>
        </div>

        {newWebhookSecret && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div className="flex-1 text-sm text-amber-900">
              {t('Secreto de firma del webhook —')}{' '}
              <strong>{t('no volverá a mostrarse')}</strong>:
              <div className="mt-1 flex items-center gap-1 font-mono text-xs break-all">
                {newWebhookSecret}
                <CopyButton value={newWebhookSecret} />
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setNewWebhookSecret(null)}
            >
              {t('Entendido')}
            </Button>
          </div>
        )}

        <form
          className="mb-6 flex flex-wrap items-end gap-3"
          onSubmit={e => {
            e.preventDefault();
            if (!webhookUrl) return;
            createWebhook.mutate({ url: webhookUrl, events: [] });
          }}
        >
          <div className="flex-1 min-w-[260px]">
            <label className="mb-1 block text-xs font-semibold text-gray-500">
              {t('URL del endpoint (HTTPS)')}
            </label>
            <input
              type="url"
              required
              value={webhookUrl}
              onChange={e => setWebhookUrl(e.target.value)}
              placeholder="https://tu-servidor.com/webhooks/suscri"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <Button type="submit" disabled={createWebhook.isPending}>
            <Plus className="mr-2 h-4 w-4" />
            {t('Añadir endpoint')}
          </Button>
        </form>

        {webhooks && webhooks.length > 0 ? (
          <div className="space-y-2">
            {webhooks.map(wh => (
              <div
                key={wh.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm text-gray-900">
                    {wh.url}
                  </p>
                  <p className="text-xs text-gray-500">
                    {wh.isActive ? t('activo') : t('desactivado')}
                    {wh.lastStatus != null &&
                      ` · ${t('último estado')} ${wh.lastStatus}`}
                    {wh.failCount > 0 &&
                      ` · ${t('{{count}} fallos seguidos', { count: wh.failCount })}`}
                    {wh.events.length > 0
                      ? ` · ${wh.events.join(', ')}`
                      : ` · ${t('todos los eventos')}`}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() =>
                      updateWebhook.mutate({
                        webhookId: wh.id,
                        isActive: !wh.isActive,
                      })
                    }
                    className={`rounded-lg p-2 ${
                      wh.isActive
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={wh.isActive ? t('Desactivar') : t('Activar')}
                  >
                    <Power className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteWebhook.mutate({ webhookId: wh.id })}
                    className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"
                    title={t('Eliminar endpoint')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">{t('Sin endpoints todavía.')}</p>
        )}
      </div>
    </div>
  );
}
